import { environment } from 'environment';
import {
  addToStorage,
  getFromStorage,
  removeFromStorage,
  StorageKey,
} from './storage-utils';

export type FlagName = keyof typeof environment.featureFlags;
export type FlagOverrides = Partial<Record<FlagName, boolean>>;

const metrics = {
  buildCount: 0,
};

const recordBuild = () => {
  if (process.env.NODE_ENV === 'test') {
    metrics.buildCount += 1;
  }
};

/**
 * Builds the storage key that keeps the feature overrides for a specific user.
 * @param userId
 */
const keyFor = (userId: string) => `featureOverrides:${userId}`;

/**
 * Reads the current user id from storage or returns a placeholder when missing.
 */
const getCurrentUserId = () => getFromStorage<string>(StorageKey.UserId) ?? '-1';

/**
 * Loads any previously stored overrides for the provided user.
 * @param userId
 */
const getStoredOverrides = (userId: string): FlagOverrides =>
  getFromStorage<FlagOverrides>(keyFor(userId)) || {};

/**
 * Converts query-string parameters into feature flag overrides.
 *
 * The URL syntax is `?feature/<flag-name>=enabled|disabled`.
 * Values matching the default are ignored so they do not pollute local storage.
 * @param existing Current override map passed in for type inference.
 */
const getParamOverrides = <T extends FlagOverrides>(existing: T): FlagOverrides => {
  const params = new URLSearchParams(window.location.search);
  const next: FlagOverrides = {};
  params.forEach((value, key) => {
    if (!key.startsWith('feature/')) return;

    const flag = key.substring('feature/'.length) as FlagName;
    const normalized = value.toLowerCase();
    const desired = normalized === 'enable' || normalized === 'enabled'
      ? true
      : normalized === 'disable' || normalized === 'disabled'
        ? false
        : undefined;
    if (desired === undefined) return;
    next[flag] = desired;
  });
  return next;
};

/**
 * Resolves the active overrides by merging storage and URL parameters, then persists the result.
 * @param userId
 */
export const buildOverrides = (userId: string): FlagOverrides => {
  recordBuild();
  const stored = getStoredOverrides(userId);
  const params = getParamOverrides(stored);
  const merged: FlagOverrides = { ...stored };

  if (Object.keys(params).length) {
    (Object.keys(params) as FlagName[]).forEach((flag) => {
      const desired = params[flag];
      const defaultValue = environment.featureFlags[flag];

      if (desired === defaultValue) {
        delete merged[flag];
      } else if (desired !== undefined) {
        merged[flag] = desired;
      }
    });

    if (Object.keys(merged).length) {
      addToStorage(keyFor(userId), merged);
    } else {
      removeFromStorage(keyFor(userId));
    }
  }

  return merged;
};

type CacheState = {
  overrides: FlagOverrides;
  userId: string;
  search: string;
};

type Listener = () => void;

const listeners = new Set<Listener>();
let cache: CacheState | undefined;
let bootstrapped = false;
let historyPatched = false;

const locationEventName = 'sg:feature-flag-locationchange';

const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

const createOverrideSignature = () => window.location.search;

const haveOverridesChanged = (next: FlagOverrides) => {
  if (!cache) return true;
  const current = cache.overrides;
  const currentKeys = Object.keys(current);
  const nextKeys = Object.keys(next);
  if (currentKeys.length !== nextKeys.length) return true;
  return nextKeys.some((key) => current[key as FlagName] !== next[key as FlagName]);
};

const applyCacheUpdate = (overrides: FlagOverrides, userId: string, search: string) => {
  const changed = !cache
    || cache.userId !== userId
    || cache.search !== search
    || haveOverridesChanged(overrides);

  cache = {
    overrides,
    userId,
    search,
  };

  return changed;
};

const computeAndStoreOverrides = (userId: string) => {
  const overrides = buildOverrides(userId);
  const search = createOverrideSignature();
  const changed = applyCacheUpdate(overrides, userId, search);
  return { overrides, changed };
};

const refreshIfCacheStale = () => {
  const userId = getCurrentUserId();
  const search = createOverrideSignature();
  if (!cache || cache.userId !== userId || cache.search !== search) {
    const { changed } = computeAndStoreOverrides(userId);
    if (changed) notifyListeners();
  }
};

const patchHistory = () => {
  if (historyPatched) return;
  historyPatched = true;
  const historyRef = window.history as History & Record<string, any>;
  (['pushState', 'replaceState'] as const).forEach((method) => {
    const original = historyRef[method];
    if (typeof original !== 'function') return;
    const typedOriginal = original as History['pushState'];
    historyRef[method] = function patchedHistoryMethod(this: History, ...args: Parameters<History['pushState']>) {
      const result = typedOriginal.apply(this, args);
      window.dispatchEvent(new Event(locationEventName));
      return result;
    };
  });
};

const handleLocationChange = () => {
  refreshIfCacheStale();
};

const handleStorageEvent = (event: StorageEvent) => {
  if (!event.key) return;
  if (event.key === StorageKey.UserId) {
    refreshOverrides();
    return;
  }

  if (cache && event.key === keyFor(cache.userId)) {
    refreshOverrides();
  }
};

export const bootstrapFeatureFlagCache = (): FlagOverrides => {
  if (bootstrapped) {
    return ensureCachePrimed();
  }

  bootstrapped = true;
  patchHistory();
  window.addEventListener('storage', handleStorageEvent);
  window.addEventListener('popstate', handleLocationChange);
  window.addEventListener('hashchange', handleLocationChange);
  window.addEventListener(locationEventName, handleLocationChange);

  const { overrides } = computeAndStoreOverrides(getCurrentUserId());
  return overrides;
};

const ensureCachePrimed = (): FlagOverrides => {
  if (!bootstrapped || !cache) {
    return bootstrapFeatureFlagCache();
  }

  refreshIfCacheStale();
  return cache.overrides;
};

export const refreshOverrides = () => {
  const { overrides, changed } = computeAndStoreOverrides(getCurrentUserId());
  if (changed) notifyListeners();
  return overrides;
};

export const getCachedOverrides = (): FlagOverrides => ensureCachePrimed();

export const getCachedOverrideNames = (): FlagName[] | undefined => {
  const overrides = ensureCachePrimed();
  const names: FlagName[] = [];

  (Object.keys(overrides) as FlagName[]).forEach((flag) => {
    if (typeof overrides[flag] === 'boolean') {
      names.push(flag);
    }
  });

  return names.length ? names : undefined;
};

export const isFeatureEnabledFromCache = (name: FlagName) => {
  const overrides = ensureCachePrimed();
  return overrides[name] ?? environment.featureFlags[name];
};

export const clearOverridesFromCache = () => {
  const userId = getCurrentUserId();
  removeFromStorage(keyFor(userId));
  const overrides = refreshOverrides();
  return overrides;
};

export const subscribeToOverrideChanges = (listener: Listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const __testing = {
  keyFor,
  getCurrentUserId,
  getStoredOverrides,
  getParamOverrides,
  handleLocationChange,
  handleStorageEvent,
  locationEventName,
  metrics,
  resetMetrics: () => { metrics.buildCount = 0; },
};





